import React, { useContext } from 'react';
import { KatSpinner } from '@amzn/katal-react';
import { logger } from 'src/logger';
import { emitClickMetrics, MetricsName } from 'src/metrics';
import AuthProvider, { AuthContext, IAuthContext } from './AuthProvider';
import fetchAuthContext from './helpers/AppContextHelper';
import { useGetUserAuthorizedResourcesQuery } from '../slice/umsServiceApi';
import { UMS_ACCOUNT_ENTITY_CLASS } from 'src/constants/GlobalConstants';
import { isRegulatorCentralApp } from './helpers/AuthAttributeHelper';

const AuthHydrater = ({ children }) => {
  const existingContext: IAuthContext = useContext(AuthContext); //existing auth context
  logger.info(`Existing AuthContext is: ${JSON.stringify(existingContext)}`);
  const { data, isError, isLoading, error } =
    useGetUserAuthorizedResourcesQuery(
      {
        identity: { userId: '' },
        resourceType: UMS_ACCOUNT_ENTITY_CLASS,
        isPublicPage: existingContext.isAnonymousUser,
        isRegulatorCentralApp: isRegulatorCentralApp,
      },
      {
        skip: !!existingContext.preferredAccountId,
      },
    );

  let authContext: IAuthContext | undefined;

  if (isLoading) {
    return <KatSpinner />;
  }

  /**
   * If authContext is populated with preferred account id then we needn't hydrate from the backend.
   * We take the context provided from the UI.
   * Applicable for Public Pages hosting Sustainability and CSAM.
   *
   * Else we do a full hydrate using fetchAuthContext
   */
  if (existingContext?.preferredAccountId) {
    authContext = existingContext;
  } else {
    authContext = fetchAuthContext(data, existingContext);
  }

  if (!authContext || isError) {
    emitClickMetrics(MetricsName.AUTH_CONTEXT, 'INVALID');
    logger.error(
      `Unable to fetch UserDetails from UMS. Check UMS API Response error: ${JSON.stringify(
        error,
      )} and UMS Response: ${JSON.stringify(data)}`,
    );
  }
  logger.info(`Auth Context populated is: ${JSON.stringify(authContext)}`);
  return <AuthProvider authContext={authContext}>{children}</AuthProvider>;
};

export default AuthHydrater;
